import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCategoriesAndMakes,
  fetchModelsByMake,
  fetchPartNamesByCategoryId,
  fetchRecentParts,
  fetchSavedParts,
  fetchSinglePart,
  getCompanyWithParts,
  removeSavedPart,
  savePart,
  searchParts
} from "./partsAction";

const initialState = {
  items: [], // Recent parts
  loading: false,
  error: null,
  singlePart: null,
  partLoading: false,
  searchResults: [], // Search results
  searchLoadingMore: false,
  isSearching: false,
  searchError: null,
  categories: [],
  makes: [],
  fetchingloading: false,
  saveLoading: false,
  vendorCompany: null,
  vendorParts: [],
  vendorLoading: false,
  removeLoading: false,
  partNames: [],
  partNamesLoading: false,
  modelLoading: false,
  partModel: [],
  getPartLoading: false,
  savedParts: [],
  // New state for storing AI search results
  aiSearchData: null
};

const partsSlice = createSlice({
  name: "parts",
  initialState,
  reducers: {
    getSavedParts: (state, action) => {
      state.savedParts = action.payload;
    },
    clearVendorData: (state) => {
      state.vendorCompany = null;
      state.vendorParts = [];
    },
    clearPartNames: (state) => {
      state.partNames = [];
    },
    clearModels: (state) => {
      state.partModel = [];
    },
    // New action to store AI search results
    storeAiSearchData: (state, action) => {
      state.aiSearchData = action.payload;
    },
    // New action to clear AI search results
    clearAiSearchData: (state) => {
      state.aiSearchData = null;
    }
  },
  extraReducers: (builder) => {
    // Recent Parts
    builder
      .addCase(fetchRecentParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentParts.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchRecentParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch recent parts";
      })
      .addCase(savePart.pending, (state) => {
        state.saveLoading = true;
      })
      .addCase(savePart.fulfilled, (state, action) => {
        state.saveLoading = false;
      })
      .addCase(savePart.rejected, (state, action) => {
        state.saveLoading = false;
      })
      .addCase(removeSavedPart.pending, (state) => {
        state.removeLoading = true;
      })
      .addCase(removeSavedPart.fulfilled, (state, action) => {
        state.removeLoading = false;
      })
      .addCase(removeSavedPart.rejected, (state, action) => {
        state.removeLoading = false;
      })
      .addCase(fetchSavedParts.pending, (state) => {
        state.getPartLoading = true;
      })
      .addCase(fetchSavedParts.fulfilled, (state, action) => {
        state.getPartLoading = false;
      })
      .addCase(fetchSavedParts.rejected, (state, action) => {
        state.getPartLoading = false;
      })

      // Search Parts
      .addCase(searchParts.pending, (state) => {
        state.searchLoadingMore = true;
        state.isSearching = true;
        state.searchError = null;
      })
      .addCase(searchParts.fulfilled, (state, action) => {
        const { results, page, totalPages } = action.payload;

        if (page > 0) {
          // Append when loading more
          state.searchResults = [...state.searchResults, ...results];
        } else {
          // Replace on first search
          state.searchResults = results;
        }

        state.page = page;
        state.totalPages = totalPages;
        state.searchLoadingMore = false;
        state.isSearching = false;
      })
      .addCase(searchParts.rejected, (state, action) => {
        state.searchLoadingMore = false;
        state.searchError = action.payload || "Failed to search parts";
        state.isSearching = false;
      })
      .addCase(fetchSinglePart.pending, (state) => {
        state.partLoading = true;
        state.error = null;
      })
      .addCase(fetchSinglePart.fulfilled, (state, action) => {
        state.partLoading = false;
        state.singlePart = action.payload;
      })
      .addCase(fetchSinglePart.rejected, (state, action) => {
        state.partLoading = false;
        state.error = action.payload;
      })
      .addCase(getCompanyWithParts.pending, (state) => {
        state.vendorLoading = true;
      })
      .addCase(getCompanyWithParts.fulfilled, (state, action) => {
        state.vendorLoading = false;
        state.vendorCompany = action.payload.company;
        state.vendorParts = action.payload.parts;
      })
      .addCase(getCompanyWithParts.rejected, (state, action) => {
        state.vendorLoading = false;
      })
      .addCase(fetchCategoriesAndMakes.pending, (state) => {
        state.fetchingloading = true;
      })
      .addCase(fetchCategoriesAndMakes.fulfilled, (state, action) => {
        state.fetchingloading = false;
        state.categories = action.payload.categories;
        state.makes = action.payload.makes;
      })
      .addCase(fetchCategoriesAndMakes.rejected, (state, action) => {
        state.fetchingloading = false;
      })
      .addCase(fetchPartNamesByCategoryId.pending, (state) => {
        state.partNamesLoading = true;
        state.error = null;
      })
      .addCase(fetchPartNamesByCategoryId.fulfilled, (state, action) => {
        state.partNamesLoading = false;
        state.partNames = action.payload;
      })
      .addCase(fetchPartNamesByCategoryId.rejected, (state, action) => {
        state.partNamesLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchModelsByMake.pending, (state, action) => {
        state.modelLoading = true;
      })
      .addCase(fetchModelsByMake.fulfilled, (state, action) => {
        state.partModel = action.payload;
        state.modelLoading = false;
      })
      .addCase(fetchModelsByMake.rejected, (state, action) => {
        state.modelLoading = false;
      });
  }
});

export const {
  getSavedParts,
  clearVendorData,
  clearPartNames,
  clearModels,
  storeAiSearchData,
  clearAiSearchData
} = partsSlice.actions;

export default partsSlice.reducer;
